/**
 * CFCA 在线续签 · 客户端控件封装（方案 A：IE-only ActiveX，照搬老系统 onLine.html/CryptoKit.js）
 *
 * 涉及两个 ActiveX 控件（仅 Windows + IE 可用）：
 *  - Cryptokit（签名控件）：选证书、读证书信息（SubjectDN/有效期等）
 *  - CertEnrollment（证书下载控件）：生成 P10、把换发的证书灌回 UKEY
 *
 * 所有方法统一返回 Promise（ActiveX 调用本身是同步的，包一层便于链式调用与统一错误处理）。
 * 老系统出处：
 *  - zcjb-bidder .../order/dymManage/views/onLine.html（续签六步链）
 *  - zcjb-bidder webdocs/js/mem/order/dymmanage/CryptoKit.js（Cryptokit 封装）
 */

import { IEVersion } from '@/util/util'

/** CFCA UKEY CSP 名称（老系统固定值，勿改） */
export const CFCA_CSP_NAME = 'CFCA FOR UKEY CSP v1.1.0'
/** 密钥长度（老系统固定值） */
export const CFCA_KEY_LENGTH = '2048'
/** 密钥算法（老系统固定值） */
export const CFCA_KEY_ALGORITHM = 'RSA'

/** 控件 CLSID 与安装包。
 * 安装包为 exe（取自老系统「下载控件」，存于 public/static/pki/，构建后原样输出）：
 *  - 签名控件 Cryptokit：CryptoKit.CEBPS.x86/x64.exe（注册 CLSID 6DC8622E/CC98DBB4，与老系统 cab 一致）
 *  - 证书下载控件 CertEnrollment：CryptoKit.CertEnrollment.Pro.x86/x64.exe
 * codebase 直接指向 exe：控件未安装时 IE 会按 codebase 提示下载安装（老系统指 cab，但 cab 资源已不可得）；
 * 已安装时 <object> 凭 classid 直接实例化 COM 控件，不请求 codebase。
 */
const CONTROL_CONFIG = {
  cryptokit: {
    id: 'cfcaCryptokitCtrl',
    x86: { clsid: '6DC8622E-6DA6-453C-8B76-BA70FF437381', codebase: './static/pki/CryptoKit.CEBPS.x86.exe' },
    x64: { clsid: 'CC98DBB4-31E8-4C43-9DD2-5DD10F1208A3', codebase: './static/pki/CryptoKit.CEBPS.x64.exe' },
  },
  certEnrollment: {
    id: 'cfcaCertEnrollmentCtrl',
    x86: { clsid: '71BB5253-EF2B-4C5B-85FF-1FD6A03C29A6', codebase: './static/pki/CryptoKit.CertEnrollment.Pro.x86.exe' },
    x64: { clsid: '9E7B8F05-ADBE-4067-ABC6-28E0230A5C18', codebase: './static/pki/CryptoKit.CertEnrollment.Pro.x64.exe' },
  },
}

/** 当前浏览器进程架构（老系统惯例：cpuClass 为 'x86' 取 32 位，其余按 64 位） */
function getArch() {
  return window.navigator.cpuClass === 'x86' ? 'x86' : 'x64'
}

/**
 * 运行环境检测：续签仅支持 Windows + IE（ActiveX 方案）。
 * 返回 { ok, reason }，ok=false 时 reason 为给用户的提示文案。
 */
export function checkEnv() {
  const platform = navigator.platform
  const isWin = platform === 'Win32' || platform === 'Windows' || platform === 'Win64'
  if (!isWin) {
    return { ok: false, reason: '非Window系统不能续签！' }
  }
  // IEVersion 返回数字(6-11)为 IE，'edge' 为 EdgeHTML 版 Edge（同样不支持 ActiveX，需拦截），-1 为非 IE
  const ie = IEVersion()
  if (ie === -1 || ie === 'edge') {
    return { ok: false, reason: '非IE浏览器不能续签！' }
  }
  return { ok: true, reason: '' }
}

/**
 * 向 body 注入 ActiveX 控件（幂等，重复调用直接返回已注入的控件对象）。
 * 老系统按 navigator.cpuClass 区分 x86/x64 加载不同 CLSID 的 cab。
 * @param {'cryptokit'|'certEnrollment'} type
 * @returns {object} 控件 DOM 对象
 */
function injectControl(type) {
  const config = CONTROL_CONFIG[type]
  const existing = document.getElementById(config.id)
  if (existing) {
    return existing
  }
  const arch = getArch()
  const { clsid, codebase } = config[arch]
  const container = document.createElement('div')
  container.style.height = '0px'
  container.innerHTML =
    '<object id="' + config.id + '" classid="clsid:' + clsid + '" codebase="' + codebase + '"></object>'
  document.body.appendChild(container)
  return document.getElementById(config.id)
}

/** 取 Cryptokit 控件对象（未注入则先注入） */
export function getCryptoKit() {
  return injectControl('cryptokit')
}

/** 取 CertEnrollment 控件对象（未注入则先注入） */
export function getCertEnrollment() {
  return injectControl('certEnrollment')
}

/**
 * 选择签名证书（会弹出 UKEY 证书选择框）。
 * 过滤器与老系统一致：仅按 CSP 名过滤，SubjectDN/IssuerDN/序列号过滤器为空。
 */
export function selectSignCert() {
  return new Promise((resolve, reject) => {
    try {
      const ctrl = getCryptoKit()
      const value = ctrl.SelectSignCert('', '', '', CFCA_CSP_NAME)
      resolve(value)
    } catch (e) {
      reject(new Error(e.message || '选择证书失败'))
    }
  })
}

/**
 * 读取当前签名证书信息。
 * @param {string} infoType 信息类型：SubjectDN / SubjectCN / IssuerDN / CertTimeFrom / CertTimeOut / SerialNumber 等
 * @returns {Promise<string>}
 */
export function getSignCertInfo(infoType) {
  return new Promise((resolve, reject) => {
    try {
      const ctrl = getCryptoKit()
      const value = ctrl.GetSignCertInfo(infoType)
      resolve(value)
    } catch (e) {
      reject(new Error(e.message || '读取证书信息失败'))
    }
  })
}

/** 读证书信息失败时取控件侧错误描述（老系统 GetLastErrorDesc 兜底） */
export function getLastErrorDesc() {
  try {
    return getCryptoKit().GetLastErrorDesc() || ''
  } catch (e) {
    return ''
  }
}

/**
 * CertEnrollment 探活：控件未安装时调用会抛异常（老系统据此提示「控件未安装」）。
 */
export function checkCertEnrollment() {
  return new Promise((resolve, reject) => {
    try {
      const ctrl = getCertEnrollment()
      ctrl.CFCA_GetVersion()
      resolve(true)
    } catch (e) {
      reject(new Error('控件未安装'))
    }
  })
}

/**
 * 生成 P10 证书请求（密钥留在 UKEY，只出请求串）。
 * 调用前按老系统顺序设置 CSP 与算法；返回 { p10, containerName }。
 * 注意（老代码注释）：只产 P10 不导入证书时最多生成 4 个密钥容器，
 * 再多需换 key 或联系 CFCA 格式化。
 * @param {string} subjectDN 证书主题 DN（取 submitCaOnline 接口返回的 dn）
 */
export function generateP10(subjectDN) {
  return new Promise((resolve, reject) => {
    try {
      const ctrl = getCertEnrollment()
      ctrl.CFCA_SetCSPInfo(CFCA_KEY_LENGTH, CFCA_CSP_NAME)
      ctrl.CFCA_SetKeyAlgorithm(CFCA_KEY_ALGORITHM)
      const p10 = ctrl.CFCA_PKCS10CertRequisition(subjectDN, 2, 0)
      const containerName = ctrl.CFCA_GetContainer()
      resolve({ p10, containerName })
    } catch (e) {
      reject(new Error(e.message || '生成P10失败'))
    }
  })
}

/**
 * 把换发的证书灌回 UKEY（签名证书 + 加密证书对都成功才算成功）。
 * @returns {Promise<true>} 任一导入失败即 reject
 */
export function importCerts({ signatureCert, encryptionCert, encryptionPrivateKey, containerName }) {
  return new Promise((resolve, reject) => {
    try {
      const ctrl = getCertEnrollment()
      const signOk = ctrl.CFCA_ImportSignCert(2, signatureCert, containerName)
      const encOk = ctrl.CFCA_ImportEncryptCert(encryptionPrivateKey, encryptionCert, containerName, 0x1 | 0x2)
      if (signOk === true && encOk === true) {
        resolve(true)
      } else {
        reject(new Error('证书导入UKEY失败'))
      }
    } catch (e) {
      reject(new Error(e.message || '证书导入UKEY失败'))
    }
  })
}
